import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MobileNavigation } from '../components/MobileNavigation';
import { Download, FileText, Users, Calendar, FilePdf, Archive, ArrowLeft, CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminExports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [exporting, setExporting] = useState(null);
  
  // Date filters for attendance export
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  useEffect(() => {
    loadSummary();
  }, []);
  
  const loadSummary = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/export/summary`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else {
        toast.error('Failed to load export summary');
      }
    } catch (error) {
      console.error('Load summary error:', error);
      toast.error('Failed to load export data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async (exportType) => {
    setExporting(exportType);
    
    try {
      let url = `${getApiBaseUrl()}/api/admin/export/${exportType}`;
      
      // Add date filters for attendance
      if (exportType === 'attendance') {
        const params = new URLSearchParams();
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        if (params.toString()) url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `${exportType}_export.csv`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
        
        toast.success(`${exportType.charAt(0).toUpperCase() + exportType.slice(1)} export downloaded successfully`);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to export ${exportType}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export ${exportType}`);
    } finally {
      setExporting(null);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-exports-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-xl font-heading">DATA EXPORTS</h1>
              <p className="text-sm text-muted-foreground">
                Download records for compliance and reporting
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Summary Stats */}
      {summary && (
        <div className="px-4 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-card border-border">
              <CardContent className="p-3 text-center">
                <Users size={24} className="mx-auto text-primary mb-1" />
                <p className="text-2xl font-bold">{summary.players_count}</p>
                <p className="text-xs text-muted-foreground">Players</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 text-center">
                <FileText size={24} className="mx-auto text-green-500 mb-1" />
                <p className="text-2xl font-bold">{summary.waivers_count}</p>
                <p className="text-xs text-muted-foreground">Waivers</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 text-center">
                <CheckCircle size={24} className="mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold">{summary.checkins_count}</p>
                <p className="text-xs text-muted-foreground">Check-ins</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-3 text-center">
                <FilePdf size={24} className="mx-auto text-red-500 mb-1" />
                <p className="text-2xl font-bold">{summary.waiver_pdfs_count}</p>
                <p className="text-xs text-muted-foreground">PDFs</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      
      {/* Export Options */}
      <div className="px-4 space-y-4">
        {/* Players Export */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={20} className="text-primary" />
              Player Roster Export
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export all registered players with parent info, emergency contacts, waiver status, and age group data.
            </p>
            <Button
              onClick={() => handleExport('players')}
              disabled={exporting === 'players'}
              className="w-full sm:w-auto"
              data-testid="export-players-btn"
            >
              {exporting === 'players' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download CSV
            </Button>
          </CardContent>
        </Card>
        
        {/* Waivers Export */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText size={20} className="text-green-500" />
              Waiver Signatures Export
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export all waiver signatures with consent details, dates, status, and compliance information.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => handleExport('waivers')}
                disabled={exporting === 'waivers'}
                data-testid="export-waivers-btn"
              >
                {exporting === 'waivers' ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Download size={16} className="mr-2" />
                )}
                Download CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('waiver-pdfs')}
                disabled={exporting === 'waiver-pdfs'}
                data-testid="export-waiver-pdfs-btn"
              >
                {exporting === 'waiver-pdfs' ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Archive size={16} className="mr-2" />
                )}
                Download All PDFs (ZIP)
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Attendance Export */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar size={20} className="text-blue-500" />
              Attendance Records Export
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Export check-in records with session details, player info, and timestamps.
            </p>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="space-y-1">
                <Label htmlFor="start-date" className="text-xs">Start Date (Optional)</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="attendance-start-date"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="end-date" className="text-xs">End Date (Optional)</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="attendance-end-date"
                />
              </div>
            </div>
            
            <Button
              onClick={() => handleExport('attendance')}
              disabled={exporting === 'attendance'}
              className="w-full sm:w-auto"
              data-testid="export-attendance-btn"
            >
              {exporting === 'attendance' ? (
                <LoadingSpinner size="sm" className="mr-2" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Download CSV {startDate || endDate ? '(Filtered)' : '(All)'}
            </Button>
          </CardContent>
        </Card>
        
        {/* Compliance Notice */}
        <Card className="bg-muted/30 border-border">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Warning size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Data Privacy Notice</p>
                <p>
                  These exports contain personal information protected under COPPA and California CCPA/CPRA. 
                  Handle all exported data securely and in accordance with your organization&apos;s data protection policies.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
